import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Tab,
  Tabs,
} from '@mui/material';
import { usePageTitle } from '@/shared/context/PageTitleContext';
import { radius } from '@/shared/theme/tokens';
import { Paper } from '@/shared/ui';
import { mergeSx } from '@/shared/ui/utils/mergeSx';
import { toolbarActionsSx } from './toolbarActionsSx';

export interface ProfilePageTab {
  label: string;
  icon?: React.ReactElement;
  disabled?: boolean;
}

export interface ProfilePageProps {
  title: string;
  tabs: ProfilePageTab[];
  tabValue: number;
  onTabChange: (value: number) => void;
  toolbarLeft?: React.ReactNode;
  toolbarRight?: React.ReactNode;
  loading?: boolean;
  loadingFallback?: React.ReactNode;
  error?: string | null;
  maxWidth?: 'md' | 'lg';
  /** Wrap children in elevated Paper. Disable for tabs that bring their own page shell. */
  contentInPaper?: boolean;
  /**
   * Fill leftover viewport height so nested `ListPage` shells can contained-scroll.
   * Account tabs stay page-scroll; list tabs (payslips / advances / time-off) need this
   * or `ListPage` collapses to 0 height (`flex-basis: 0` + `overflow: hidden`).
   */
  fillHeight?: boolean;
  /**
   * Child is its own `ListPage`. Skip this page's padded container so that list's
   * container lines up with the tab strip, and its toolbar keeps the usual 16px gap.
   */
  nestedList?: boolean;
  children: React.ReactNode;
}

const ProfilePage: React.FC<ProfilePageProps> = ({
  title,
  tabs,
  tabValue,
  onTabChange,
  toolbarLeft,
  toolbarRight,
  loading = false,
  loadingFallback,
  error,
  maxWidth = 'lg',
  contentInPaper = true,
  fillHeight = false,
  nestedList = false,
  children,
}) => {
  usePageTitle(title);

  const showToolbar = Boolean(toolbarLeft || toolbarRight);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number): void => {
    onTabChange(newValue);
  };

  if (loading) {
    return (
      <Container maxWidth={maxWidth}>
        <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', py: 8 }}>
          {loadingFallback ?? <CircularProgress />}
        </Box>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        ...(fillHeight
          ? {
              flex: '1 1 0',
              width: '100%',
              alignSelf: 'stretch',
              overflow: 'hidden',
            }
          : {}),
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: '#fff',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Container maxWidth={maxWidth}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="profile tabs"
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.label}
                label={tab.label}
                icon={tab.icon}
                iconPosition={tab.icon ? 'start' : undefined}
                disabled={tab.disabled}
                sx={{ textTransform: 'none' }}
              />
            ))}
          </Tabs>
        </Container>
      </Box>

      {showToolbar && (
        <Container maxWidth={maxWidth}>
          <Box
            sx={mergeSx(
              {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                mt: 2,
                mb: 1,
              },
              toolbarActionsSx,
            )}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
              }}
            >
              {toolbarLeft}
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              {toolbarRight}
            </Box>
          </Box>
        </Container>
      )}

      {nestedList ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: '100%',
          }}
        >
          {error && (
            <Container maxWidth={maxWidth} sx={{ flexShrink: 0 }}>
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            </Container>
          )}
          {children}
        </Box>
      ) : (
        <Container
          maxWidth={maxWidth}
          sx={{
            mt: showToolbar ? 0 : 3,
            mb: fillHeight ? 3 : 4,
            ...(fillHeight
              ? {
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }
              : {}),
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
              {error}
            </Alert>
          )}

          {contentInPaper ? (
            <Paper elevation={0} sx={{ p: 3, borderRadius: radius.sm }}>
              {children}
            </Paper>
          ) : fillHeight ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {children}
            </Box>
          ) : (
            children
          )}
        </Container>
      )}
    </Box>
  );
};

export default ProfilePage;
